import { Injectable } from '@angular/core';
import { ShipmentLocationsService, SHIPMENT_LOCATION_TYPE_ID } from '@cemex-core/cmx-api';
import { Store, select } from '@ngrx/store';
import { SetJobsites, SetJobsiteLoader, SetAllJobsites } from '../store/actions';
import { take } from 'rxjs/operators';
import { selectFilterState } from '../store/selectors';
import { Jobsite } from '../models';
import { GlobalFiltersState } from '../models';

@Injectable({
  providedIn: 'root',
})
export class GlobalFiltersJobsitesService {
  constructor(private jobsitesService: ShipmentLocationsService, private store: Store<GlobalFiltersState>) {}

  async getJobsites(legalEntityId: number) {
    const shouldFetch = await this.shouldFetchJobsites(legalEntityId);
    if (!shouldFetch) {
      return;
    }
    try {
      this.store.dispatch(new SetJobsiteLoader(true));
      const [jobsites, allJobsites] = await this.fetchAndMapJobsites(legalEntityId);
      this.store.dispatch(new SetJobsites(legalEntityId, jobsites.length > 0 ? jobsites : allJobsites));
      this.store.dispatch(new SetAllJobsites(legalEntityId, allJobsites));
      this.store.dispatch(new SetJobsiteLoader(false));
    } catch (error) {
      this.store.dispatch(new SetJobsiteLoader(false));
    }
  }

  private async shouldFetchJobsites(legalEntityId: number): Promise<boolean> {
    try {
      const filterState = await this.store
        .pipe(
          select(selectFilterState),
          take(1),
        )
        .toPromise();
      const clientsFilters = filterState.filterPerCustomer[legalEntityId];
      if (clientsFilters.jobsites && clientsFilters.allJobsites) {
        return false;
      }
      return true;
    } catch (error) {
      return true;
    }
  }

  private async fetchAndMapJobsites(legalEntityId: number): Promise<[Jobsite[], Jobsite[]]> {
    const jobsiteResponses = await Promise.all([
      this.fetchTop5Jobsites(legalEntityId),
      this.fetchAllJobsites(legalEntityId),
    ]);
    const mappedJobsites = jobsiteResponses.map(response => {
      return response.shipmentLocations.map(jobsite => {
        return {
          code: jobsite.shipmentLocationCode,
          id: jobsite.shipmentLocationId,
          name: jobsite.shipmentLocationDesc,
        };
      });
    });
    return [mappedJobsites[0], mappedJobsites[1]];
  }

  private fetchTop5Jobsites(legalEntityId: number) {
    return this.jobsitesService
      .getShipmentLocations({
        legalEntityId: {
          entityId: legalEntityId,
          entityTypeId: SHIPMENT_LOCATION_TYPE_ID.CUSTOMER,
        },
        shipmentLocationTypeCode: 'J',
        lastOrdered: true,
      } as any)
      .toPromise();
  }

  private fetchAllJobsites(legalEntityId: number) {
    return this.jobsitesService
      .getShipmentLocations({
        legalEntityId: {
          entityId: legalEntityId,
          entityTypeId: SHIPMENT_LOCATION_TYPE_ID.CUSTOMER,
        },
        shipmentLocationTypeCode: 'J',
      })
      .toPromise();
  }
}
